import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Download, Star, Zap, FileText, Package } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useI18n } from '@/i18n';
import { getCurrentTier, TIERS } from '@/tiers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Pricing = () => {
  const { t } = useI18n();
  const [loadingPackage, setLoadingPackage] = useState(null);
  const currentTier = getCurrentTier();

  const handleCheckout = async (packageId) => {
    setLoadingPackage(packageId);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        package_id: packageId,
        origin_url: window.location.origin
      });
      localStorage.setItem('pending_package_id', packageId);
      window.location.href = res.data.url;
    } catch (e) {
      console.error(e);
      toast.error('Failed to start checkout. Please try again.');
      setLoadingPackage(null);
    }
  };

  const basicFeatures = [
    '15 ' + t('exercises'),
    t('stepByStep'),
    t('videoDemo'),
    t('proTips'),
    t('favorites'),
  ];

  const proFeatures = [
    '25 ' + t('exercises'),
    t('downloadDrillCard'),
    t('printReady'),
    t('offlineAccess'),
  ];

  const ultimateFeatures = [
    '38 ' + t('exercises') + ' (' + t('allDrills') + ')',
    t('downloadAllZip'),
    'Share with team',
    'Everything in Pro',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-blue-50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14 space-y-4">
          <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 text-sm font-semibold border-0">
            <Crown className="h-4 w-4 inline mr-1" />
            {t('unlockPremium')}
          </Badge>
          <h1 className="text-5xl md:text-6xl font-black text-gray-900">
            {t('pricingTitle')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">{t('pricingTitleEm')}</span>{' '}
            {t('pricingTitleEnd')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t('pricingSubtitle')}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {/* Basic Plan */}
          <Card className={`border-2 ${currentTier === TIERS.BASIC ? 'border-gray-400' : 'border-gray-300'} bg-white shadow-lg relative`} data-testid="plan-basic">
            {currentTier === TIERS.BASIC && (
              <div className="absolute top-0 right-0 bg-gray-700 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">
                {t('yourCurrentPlan')}
              </div>
            )}
            <CardContent className="p-7 space-y-5">
              <div>
                <h3 className="text-2xl font-black text-gray-900">{t('basicPlan')}</h3>
                <p className="text-gray-500 mt-1 text-sm">{t('basicTagline')}</p>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-black text-gray-900">$0</span>
                <span className="text-gray-500">CAD · {t('forever')}</span>
              </div>
              <ul className="space-y-2.5">
                {basicFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                disabled
                variant="outline"
                className="w-full border-2 border-gray-300 text-gray-500 font-bold py-5"
                data-testid="basic-plan-btn"
              >
                {t('currentPlan')}
              </Button>
            </CardContent>
          </Card>

          {/* Pro Plan */}
          <Card className="border-4 border-orange-400 bg-gradient-to-br from-orange-50 to-yellow-50 shadow-2xl relative overflow-hidden" data-testid="plan-pro">
            <div className="absolute top-0 right-0 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">
              POPULAR
            </div>
            <CardContent className="p-7 space-y-5">
              <div>
                <h3 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                  <Crown className="h-6 w-6 text-orange-500" />
                  {t('proPlan')}
                </h3>
                <p className="text-gray-600 mt-1 text-sm">{t('proTagline')}</p>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">$9.99</span>
                <span className="text-gray-600">CAD · {t('oneTime')}</span>
              </div>
              <ul className="space-y-2.5">
                {proFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-800 font-medium text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              {currentTier === TIERS.PRO || currentTier === TIERS.ULTIMATE ? (
                <Button disabled className="w-full bg-green-600 text-white font-bold py-5 text-lg" data-testid="pro-unlocked-btn">
                  <Check className="mr-2 h-5 w-5" />
                  {t('proUnlocked')}
                </Button>
              ) : (
                <Button
                  onClick={() => handleCheckout('pro_pack')}
                  disabled={loadingPackage !== null}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-5 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                  data-testid="pro-checkout-btn"
                >
                  {loadingPackage === 'pro_pack' ? '...' : (
                    <>
                      <Zap className="mr-2 h-5 w-5" />
                      {t('upgradeToPro')} - $9.99 CAD
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Ultimate Plan */}
          <Card className="border-4 border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-2xl relative overflow-hidden" data-testid="plan-ultimate">
            <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">
              {t('bestValue')}
            </div>
            <CardContent className="p-7 space-y-5">
              <div>
                <h3 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                  <Package className="h-6 w-6 text-purple-600" />
                  {t('ultimatePlan')}
                </h3>
                <p className="text-gray-600 mt-1 text-sm">{t('ultimateTagline')}</p>
              </div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-2xl font-bold text-gray-400 line-through">$19.99</span>
                <span className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">$14.99</span>
                <span className="text-gray-600">CAD · {t('oneTime')}</span>
              </div>
              <Badge className="bg-yellow-300 text-yellow-900 font-bold border-0 text-sm px-3 py-1 w-fit shadow-md" data-testid="ultimate-save-badge">
                💰 {t('save5')}
              </Badge>
              <ul className="space-y-2.5">
                {ultimateFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-800 font-medium text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              {currentTier === TIERS.ULTIMATE ? (
                <Button disabled className="w-full bg-green-600 text-white font-bold py-5 text-lg" data-testid="ultimate-unlocked-btn">
                  <Check className="mr-2 h-5 w-5" />
                  {t('bundleUnlocked')}
                </Button>
              ) : (
                <Button
                  onClick={() => handleCheckout('bundle_pack')}
                  disabled={loadingPackage !== null}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-5 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                  data-testid="ultimate-checkout-btn"
                >
                  {loadingPackage === 'bundle_pack' ? '...' : (
                    <>
                      <Package className="mr-2 h-5 w-5" />
                      {t('upgradeToUltimate')} - $14.99 CAD
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Features Highlight */}
        <div className="mt-20 max-w-4xl mx-auto">
          <h2 className="text-3xl font-black text-center text-gray-900 mb-10">{t('whatsIn')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 border-orange-200 bg-white hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-14 h-14 mx-auto bg-orange-100 rounded-xl flex items-center justify-center">
                  <FileText className="h-7 w-7 text-orange-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900">{t('printReady')}</h3>
                <p className="text-sm text-gray-600">{t('printReadyDesc')}</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-200 bg-white hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-14 h-14 mx-auto bg-blue-100 rounded-xl flex items-center justify-center">
                  <Download className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900">{t('offlineAccess')}</h3>
                <p className="text-sm text-gray-600">{t('offlineDesc')}</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-yellow-200 bg-white hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-14 h-14 mx-auto bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Star className="h-7 w-7 text-yellow-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900">{t('allDrills')}</h3>
                <p className="text-sm text-gray-600">{t('allDrillsDesc')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
